import { AI_AGENT_REPORT_TYPE } from '@/ai-agents/components/ai-agents.constants';
import { AppsService } from '@/common/apps/apps.service.js';
import { LOADING_STATES } from '@/constants';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { getWhitelistedGroups } from '@/metadata/utils';
import { isMobileApp } from '@/utils/apps';
import { titlecase } from '@/utils/string.utils';
import { i18n_t as $t } from '@utils/i18n';
import moment, { DATE_FORMAT } from '@utils/moment-wrapper';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import identity from 'lodash/identity';
import uniqBy from 'lodash/uniqBy';
import upperFirst from 'lodash/upperFirst';

import { getActiveApplicationsMap } from '@/data/session/session-globals.ts';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { connectAggregationToObservable, createRejectedStatus, getRows } from '@pendo/agg-connect';
import { Subject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as reportUtils from './utils';

export const DATE_CUTOFF = new Date('2018-01-01');

export class ReportError extends Error {
    constructor ({ message = 'We were unable to run your report.', retriable = true } = {}) {
        super(message);
        this.message = message;
        this.retriable = retriable;
    }
}

export const progressCircleColors = {
    center: { fill: 'var(--palette-gray-0)', stroke: 'var(--palette-gray-0)' },
    default: { fill: 'var(--palette-gray-0)', stroke: 'var(--palette-gray-0)' },
    progress: { fill: 'var(--palette-teal-70)', stroke: 'var(--palette-teal-70)' },
    complete: { fill: 'var(--palette-teal-70)', stroke: 'var(--palette-teal-70)' },
};

export function isImpersonating () {
    return store.getters['auth/isImpersonating'];
}

export function updateReport (report, skipVersioning) {
    return store.dispatch('savedReports/update', { report, skipVersioning });
}

export function fetchReport (reportId) {
    return store.dispatch('savedReports/loadOne', {
        id: reportId,
        reFetch: true,
    });
}

export function commitUpdatedReport (report) {
    store.commit('savedReports/setMapAtKey', { id: report.id, report });
}

export function getReport (reportId) {
    return store.getters['savedReports/reportById'](reportId);
}

export function applySchema (name, { schema, pollId, polls }) {
    const goFormat = {
        date: '2006-01-02',
        time: '2006-01-02 15:04:05',
    };
    let transformedSchema = '';

    switch (schema.toLowerCase()) {
        case 'date':
        case 'time':
            transformedSchema = {
                [name]: `if(${name}==0||isNull(${name}),"",formatTime("${goFormat[schema]}",${name}))`,
            };
            break;
        case 'prettytime':
            transformedSchema = { [name]: `if(${name}==0,"--",${name}/1000)` };
            break;
        case 'number':
            transformedSchema = { [name]: `if(isNil(${name}), 0, ${name})` };
            break;
        case 'poll': {
            if (polls && pollId) {
                const poll = find(polls, ['id', pollId]);
                const idResponses = poll && poll.idResponses;
                if (idResponses) {
                    const PLACEHOLDER = '{ELSE}';
                    let idResponseMap = PLACEHOLDER;
                    forEach(idResponses, (idResponse, id) => {
                        idResponseMap = idResponseMap.replace(
                            PLACEHOLDER,
                            `if(${name}=="${id}","${idResponse}",${PLACEHOLDER})`,
                        );
                    });

                    idResponseMap = idResponseMap.replace(PLACEHOLDER, name);
                    transformedSchema = { [name]: idResponseMap };
                    break;
                }
            }
        }
        // eslint-disable-next-line no-fallthrough
        default:
            transformedSchema = name;
            break;
    }

    return transformedSchema;
}

export function isOlderThanX (timestamp, count, granularity) {
    if (!timestamp) return false;

    return moment(timestamp).isBefore(moment().subtract(count, granularity), granularity);
}

export function timeSeriesIncludesToday (timeSeries) {
    if (timeSeries.type === 'Today') return true;
    if (timeSeries.type === 'Custom') {
        return moment().isBetween(timeSeries.start, timeSeries.end, 'day');
    }

    return false;
}

export function usesMovingTimeseries (timeSeries) {
    return timeSeries.type !== 'Custom';
}

export function isStale (report) {
    // never auto-run when impersonating
    if (reportUtils.isImpersonating()) return false;
    const {
        lastSuccessRunAt,
        currentRunningAggStart,
        definition: { timeSeries },
    } = report;

    // if there's an agg running, use it unless it has been running for over 60mins.
    if (currentRunningAggStart && !isOlderThanX(currentRunningAggStart, 60, 'minute')) {
        return false;
    }

    // if there's a cached report, use it unless it has expired or has a "moving" timeSeries (Last 7 Days, etc)
    if (lastSuccessRunAt) {
        // Exception with funnel and path reports as they are expensive and it should use the cached report as long as it is not expired
        if (reportUtils.isFunnelReport(report) || reportUtils.isPathReport(report)) {
            return isOlderThanX(lastSuccessRunAt, 14, 'days');
        }

        return (
            isOlderThanX(lastSuccessRunAt, 14, 'days') ||
            (timeSeriesIncludesToday(timeSeries) && isOlderThanX(lastSuccessRunAt, 15, 'minute')) ||
            (usesMovingTimeseries(timeSeries) && isOlderThanX(lastSuccessRunAt, 0, 'day'))
        );
    }

    // no running or cached agg means we must rerun
    return true;
}

function isTimeSeriesStartBeforeCutoff (path) {
    const startDate = new Date(get(path, ['definition', 'timeSeries', 'start']));

    return path.definition.timeSeries.type === 'Custom' && startDate < DATE_CUTOFF;
}

async function validateReport (validate, reportId) {
    const report = reportUtils.getReport(reportId);
    if (!report) {
        throw new ReportError({
            message: 'Cannot find the report.',
            retriable: false,
        });
    }

    if (isTimeSeriesStartBeforeCutoff(report)) {
        throw new ReportError({
            message:
                'Paths & Funnels no longer support date ranges earlier than Jan 1, 2018. Please update your date range.',
            retriable: false,
        });
    }

    await validate(report);
}

export function buildReportStream (
    { aggregation, validate = identity, buildAggParams = identity, processResponse = identity },
    ...responseArgs
) {
    const subject = new Subject();
    const response$ = connectAggregationToObservable(
        aggregation,
        subject.pipe(
            mergeMap(async ([reportId, forceRun]) => {
                try {
                    await validateReport(validate, reportId);
                } catch (error) {
                    return createRejectedStatus(error);
                }

                const report = reportUtils.getReport(reportId);

                return {
                    report,
                    forceRun: forceRun || reportUtils.isStale(report),
                    impersonating: isImpersonating(),
                    ...buildAggParams(report),
                };
            }),
        ),
    ).pipe(
        mergeMap(async (response) => {
            const { LOADING, NOTIFICATION, REJECTED, RESOLVED } = LOADING_STATES;
            let report;
            let data;
            switch (response.status) {
                case LOADING:
                    return response;
                case NOTIFICATION:
                    return response;
                case REJECTED:
                    return response.error instanceof ReportError
                        ? response
                        : {
                            ...response,
                            error: new ReportError(),
                        };
                case RESOLVED:
                    report = response.value.report;
                    data = getRows(response.value.data);
                    reportUtils.commitUpdatedReport(report);

                    return {
                        status: response.status,
                        value: processResponse(report, data, ...responseArgs),
                    };
            }
        }),
    );

    return [response$, subject];
}

export function getRouteNameFromType (reportType) {
    switch (reportType) {
        case 'Saved Reports':
        case 'Data Explorer':
            return 'DataExplorer';
        case 'Path':
            return 'PathDetails';
        case 'Funnel':
            return 'FunnelDetails';
        case 'Saved Retention Reports':
        case 'Retention':
            return 'RetentionDetails';
        case 'Workflow':
            return 'WorkflowDetails';
        case AI_AGENT_REPORT_TYPE:
            return 'AiAgentReport';
        case 'Visitor':
            return 'VisitorListReport';
        case 'Account':
            return 'AccountListReport';
        case 'ParentAccount':
            return 'ParentAccountListReport';
        default:
            return '';
    }
}

export function lastNDaysTimeseries (numDays) {
    const start = moment()
        .subtract(numDays, 'day')
        .startOf('day')
        .format(DATE_FORMAT.iso)
        .valueOf();
    const end = moment()
        .subtract(1, 'days')
        .startOf('day')
        .format(DATE_FORMAT.iso)
        .valueOf();

    return { type: `Last ${numDays} Days`, start, end };
}

export function getDefaultTimeseries (reportType) {
    switch (reportType) {
        case 'Workflow':
            return lastNDaysTimeseries(7);
        default:
            return lastNDaysTimeseries(30);
    }
}

export function buildQueryBuilderTimeSeries (timeSeries) {
    // paths/funnels created in angular add a timeSeries that
    // no longer works with the new time-series-picker
    const { type } = timeSeries;
    let { start, end } = timeSeries;
    if (Number.isInteger(start)) {
        start = moment(start)
            .format(DATE_FORMAT.iso)
            .valueOf();
        end = moment(end)
            .format(DATE_FORMAT.iso)
            .valueOf();
    }

    return {
        config: {
            type,
            start: type === 'Custom' ? start : undefined,
            end: type === 'Custom' ? end : undefined,
        },
        // time-series-picker expects this to be defined...
        // but it doesn't matter what it is
        spec: {},
    };
}

export function getReportType (report) {
    if (report.kind === 'Workflow') {
        return 'Workflow';
    }

    // Old Path/Funnel reports have `type` in `definition` object.
    return report?.type || report?.definition?.type;
}

export function isRetentionReport (report) {
    return ['Saved Retention Reports', 'Retention'].includes(getReportType(report));
}

export function isDataExplorerReport (report) {
    return ['Saved Reports', 'Data Explorer'].includes(getReportType(report));
}

export function isFunnelReport (report) {
    return ['Funnel'].includes(getReportType(report));
}

export function isPathReport (report) {
    return ['Path'].includes(getReportType(report));
}

export function getMobileProperties (targetEntities = []) {
    const { ALL_PLATFORM, ALL, MOBILE_PLATFORM, ALL_AGG_ID, MOBILE_AGG_ID } = AppsService;
    const ALL_PLATFORM_IDS = [ALL_PLATFORM, ALL, ALL_AGG_ID];
    const MOBILE_PLATFORMS_IDS = [MOBILE_PLATFORM, MOBILE_AGG_ID];
    const isAllMobileAppsSelected = MOBILE_PLATFORMS_IDS.includes(targetEntities[0]?.id);

    if (isAllMobileAppsSelected) {
        return { includes_mobile: true };
    }

    const isAllAppsSelected = ALL_PLATFORM_IDS.includes(targetEntities[0]?.id);
    const hasIntegratedMobileApps = store.getters['apps/integratedMobileList']?.length > 0;

    if (isAllAppsSelected && hasIntegratedMobileApps) {
        return { includes_mobile: true };
    }

    const appMap = getActiveApplicationsMap();
    const selectedApps = targetEntities.map((entity) => {
        const appId = entity.appId || entity.id;

        return appMap[appId];
    });

    let selectedMobileApps = selectedApps.filter((app) => isMobileApp(app?.platform));
    selectedMobileApps = uniqBy(selectedMobileApps, 'id');
    if (selectedMobileApps.length === 1) {
        const selectedMobileApp = selectedMobileApps[0];

        return {
            mobile_app_name: selectedMobileApp.name,
            mobile_platform: selectedMobileApp.platform,
            mobile_framework: selectedMobileApp.framework,
            mobile_framework_type: get(selectedMobileApp, 'mobileFrameworkFlavour.type', undefined),
            mobile_sdk_version: get(selectedMobileApp, 'mobileFrameworkFlavour.sdkVersion', undefined),
            mobile_plugin_version: get(selectedMobileApp, 'mobileFrameworkFlavour.pluginVersion', undefined),
            includes_mobile: true,
        };
    }

    return { includes_mobile: selectedMobileApps.length > 1 };
}

/**
 * Creates a function that marks rules as duplicates in
 * a list of metadata rules based on a list of fields.
 * @param {...string} field
 * @return {Function}
 */

function markDuplicates (...args) {
    return function (metadataList) {
        const groups = metadataList.reduce((acc, rule) => {
            const key = args.map((field) => rule[field]).join(';');
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(rule);

            return acc;
        }, {});

        Object.values(groups).forEach((ruleGroup) => {
            if (ruleGroup.length > 1) {
                ruleGroup.forEach((rule) => {
                    rule.duplicate = true;
                });
            }
        });

        return metadataList;
    };
}

function handleMultipleApps (collection, itemFn) {
    if (store.getters['apps/isMultiApp']) {
        collection = collection.map((item) => itemFn(item));
    }

    return collection;
}

export function getDefaultKindOptions (kind) {
    const defaultKindOptions = [
        {
            optionLabel: $t('Events'),
            functionName: 'eventCount',
            id: 'eventCount',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('Dead clicks'),
            functionName: 'deadClicks',
            id: 'deadClicks',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('Error clicks'),
            functionName: 'errorClicks',
            id: 'errorClicks',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('Rage clicks'),
            functionName: 'rageClicks',
            id: 'rageClicks',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('U-turns'),
            functionName: 'uTurns',
            id: 'uTurns',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('Days Active'),
            functionName: 'daysActive',
            id: 'daysActive',
            schema: 'number',
            kind,
        },
        {
            optionLabel: store.getters['apps/isMultiApp'] ? $t('Time on App (minutes)') : $t('Time on Site (minutes)'),
            functionName: 'eventTime',
            id: 'eventTime',
            schema: 'number',
            kind,
        },
        {
            optionLabel: store.getters['apps/isMultiApp'] ? $t('Time on App Trending') : $t('Time on Site Trending'),
            functionName: 'eventTimeTrending',
            id: 'eventTimeTrending',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('Average Time Per Day (minutes)'),
            functionName: 'avgTimePerDay',
            id: 'avgTimePerDay',
            schema: 'number',
            kind,
        },
        {
            optionLabel: $t('Average Time Per Day Trending'),
            functionName: 'avgTimePerDayTrending',
            id: 'avgTimePerDayTrending',
            schema: 'number',
            kind,
        },
        {
            optionLabel: store.getters['apps/isMultiApp']
                ? $t('Usage Trending (Deprecated. Use Time on App Trending instead)')
                : $t('Usage Trending (Deprecated. Use Time on Site Trending instead)'),
            functionName: 'usageTrending',
            id: 'usageTrending',
            schema: 'trending',
            kind,
            disabled: true,
        },
    ];

    if (kind !== 'visitor') {
        defaultKindOptions.push({
            optionLabel: $t('Visitors'),
            functionName: 'numVisitors',
            id: 'numVisitors',
            schema: 'number',
            kind,
        });
    }

    if (kind === 'account' || kind === 'parentAccount') {
        defaultKindOptions.push({
            optionLabel: $t('Description'),
            functionName: 'metadataField',
            id: `${kind}Description`,
            field: `${kind}.pendo.description`,
            schema: 'string',
            kind,
            sortable: false,
        });
    }

    if (kind === 'visitor' && hasSegmentFlag('emailVisitorsReport')) {
        defaultKindOptions.push({
            optionLabel: $t('Has bounced from email'),
            functionName: 'metadataField',
            id: 'isBounced',
            schema: 'boolean',
            field: 'visitor.pendo.campaignemailstatus == "bounced"',
            kind,
        });
        defaultKindOptions.push({
            optionLabel: $t('Has email spam complaint'),
            functionName: 'metadataField',
            id: 'isMarkedAsSpam',
            schema: 'boolean',
            field: 'visitor.pendo.campaignemailstatus == "complaint"',
            kind,
        });
        defaultKindOptions.push({
            optionLabel: $t('Has unsubscribed from email'),
            functionName: 'metadataField',
            id: 'isUnsubscribed',
            schema: 'boolean',
            field: 'visitor.pendo.campaignemailstatus == "unsubscribed"',
            kind,
        });
    }

    return defaultKindOptions;
}

function filterRules (rules, kind) {
    return rules
        .filter((rule) => {
            if (kind === 'visitor') {
                return rule.field !== 'visitor.auto.accountid' && rule.field !== 'visitor.auto.id';
            }

            if (kind === 'account') {
                return rule.field !== 'account.auto.id';
            }

            return true;
        })
        .filter((rule) => {
            return kind === 'visitor' ? true : rule.kind === kind;
        });
}

export function isProductRule (option) {
    const id = (option && option.id) || option;

    return id === 'page' || id === 'feature' || id === 'trackType' || id === 'poll' || id === 'guide' || id === 'email';
}

export function isVisitorRule (option) {
    return !isProductRule(option) && option.kind === 'visitor';
}

function isAccountRule (option) {
    return !isProductRule(option) && option.kind === 'account';
}

function isParentAccountRule (option) {
    return !isProductRule(option) && option.kind === 'parentAccount';
}

export function loadKindOptions (kind, metadata) {
    const whitelistOptions = { editableOnly: false, groupsArray: true };
    const groups = metadata.map((schema) => schema.group);
    const whitelist = getWhitelistedGroups(groups, whitelistOptions);
    metadata = metadata
        .filter((data) => whitelist.includes(data?.group))
        .map(({ group, kind, field, Type, DisplayName, ElementType }) => ({
            kind,
            optionLabel: DisplayName || field,
            field: `${kind}.${group}.${field}`,
            id: `${kind}_${group}_${field}`,
            functionName: 'metadataField',
            schema: Type || 'string',
            group,
            elementType: ElementType,
        }))
        .concat([
            {
                kind: 'visitor',
                optionLabel: 'Sample Group',
                field: 'visitor.auto.idhash%100',
                id: 'visitor_sample_group',
                functionName: 'metadataField',
                schema: 'number',
                group: 'auto',
            },
        ]);
    metadata = markDuplicates('kind', 'optionLabel')(metadata);
    metadata = handleMultipleApps(metadata, (item) => {
        item.modifiedOptionLabel = $t('metadataFieldAllApps', { columnLabel: item.optionLabel });

        return item;
    });
    const defaultKindOptions = getDefaultKindOptions(kind);
    metadata = defaultKindOptions.concat(filterRules(metadata, kind));

    const kindOptions = metadata;
    const visitorOptions = kindOptions.filter(isVisitorRule);
    const accountOptions = kindOptions.filter(isAccountRule);
    const parentAccountOptions = kindOptions.filter(isParentAccountRule);

    return { kindOptions, visitorOptions, accountOptions, parentAccountOptions };
}

export function getProductOptions (kind = null) {
    const options = {
        page: {
            id: 'page',
            optionLabel: $t('Page'),
            labels: {
                page_eventCount: $t('Page Views'),
                page_numVisitors: $t('Visitors'),
                page_daysActive: $t('Days Active'),
                page_uTurns: $t('U-turns'),
                page_deadClicks: $t('Dead clicks'),
                page_errorClicks: $t('Error clicks'),
                page_rageClicks: $t('Rage clicks'),
                page_eventTime: $t('Time On Page (minutes)'),
            },
        },
        feature: {
            id: 'feature',
            optionLabel: $t('Feature'),
            labels: {
                feature_eventCount: $t('Clicks'),
                feature_numVisitors: $t('Visitors'),
                feature_daysActive: $t('Days Active'),
                feature_deadClicks: $t('Dead clicks'),
                feature_errorClicks: $t('Error clicks'),
                feature_rageClicks: $t('Rage clicks'),
            },
        },
        trackType: {
            id: 'trackType',
            optionLabel: $t('Track Events'),
            labels: {
                trackTypeDaysActive: $t('Days Active'),
                trackTypeEventCount: $t('Event Count'),
                trackTypeNumVisitors: $t('Visitors'),
            },
        },
        guide: {
            id: 'guide',
            optionLabel: $t('Guide'),
            labels: {
                guideViews: $t('Guide Step Views'),
                guide_numVisitors: $t('Visitors'),
            },
        },
        poll: {
            id: 'poll',
            optionLabel: $t('Poll'),
            labels: {
                pollTime: $t('Latest Submission Date'),
                pollResponseAllTime: $t('Latest Response (all time)'),
                pollResponseWithinDateRange: $t('Latest Response (within Date Range specified)'),
            },
        },
        email: {
            id: 'email',
            optionLabel: $t('Email'),
            icon: { type: 'mail' },
            labels: {
                email_delivered: $t('Delivered'),
                email_opened: $t('Opened'),
                email_clicked: $t('Clicked'),
                email_deliveredCount: $t('Total delivered emails'),
                email_deliveredLastDate: $t('Last delivered date'),
                email_openedCount: $t('Total opened emails'),
                email_openedLastDate: $t('Last opened date'),
                email_clickedCount: $t('Total clicked emails'),
                email_clickedLastDate: $t('Last clicked date'),
                emails_deliveredCount: $t('Total delivered emails'),
                emails_deliveredLastDate: $t('Last delivered date'),
                emails_openedCount: $t('Total opened emails'),
                emails_openedLastDate: $t('Last opened date'),
                emails_clickedCount: $t('Total clicked emails'),
                emails_clickedLastDate: $t('Last clicked date'),
            },
        },
    };

    // filter out email options if not a visitor
    if (kind !== 'visitor' || !hasSegmentFlag('emailVisitorsReport')) {
        const { email, ...filteredOptions } = options;

        return filteredOptions;
    }

    return options;
}

// the key is whatever we are grouping on in the aggregation
// so typically "visitor", "account", or "parentAccount".
// this column is always the FIRST column in the list and not deletable,
// so it is `immutable`.
export function keyIdColumn ({ key, ...rest }) {
    const column = {
        isDefault: true,
        functionName: `${key}IdColumn`,
        columnName: `${key}Id`,
        columnLabel: `${titlecase(key)} ID`,
        schema: 'string',
        immutable: true,
        primaryKeys: [`${key}Id`],
        link: keyIdColumnLink(key),
        kind: key,
    };

    return Object.assign(column, rest);
}

function keyIdColumnLink (key) {
    return (value) => `/${key}/${encodeURIComponent(value)}`;
}

// name is something like "eventCount" that we will use to map to agg2 columns
// key the thing we group on in the aggregation ie visitor, account, or parentAccount
// entityType is anything like page, feature, guide, poll, trackType, or agentic
// entity is the actual object, which we use for its name and id.
export function aggregationColumn ({ name, key, entityType, entity, app, parameterId, ...rest }) {
    const functionName = buildFunctionName(name, entityType);
    const column = {
        // maps directly to a column function in "@pendo/aggregations" (agg2)
        functionName,
        // used to uniquely identify the value in the aggregation
        columnName: entity ? `${entityType}_${entity.id}_${entityType}_${name}` : name,
        // used to determine what we are grouping on in the aggregation
        // important if we have both account AND visitor columns
        primaryKeys: [`${key}Id`],
        // used to label the column header
        columnLabel: buildColumnLabel({ functionName, entityType, entity, app, parameterId }),
        kind: key,
        parameterId,
    };

    column.columnName = column.columnName.replace(/-/g, '_');

    if (entity) {
        // For agentic, use agentId instead of agenticId
        const entityIdKey = entityType === 'agentic' ? 'agentId' : `${entityType}Id`;
        column[entityIdKey] = entity.id;
    }

    return Object.assign(column, rest);
}

function buildColumnLabel ({ functionName, entityType, entity, app, parameterId }) {
    const labelMap = getDefaultKindOptions().reduce((map, option) => {
        map[option.functionName] = option.optionLabel;

        return map;
    }, {});

    const defaultLabel = get(labelMap, functionName, functionName);
    let label = entityType
        ? `${get(getProductOptions(), `${entityType}.labels.${functionName}`, defaultLabel)} for ${entity.name}`
        : defaultLabel;

    if (app) {
        const appPlatformString = app.platform ? ` - ${app.platform}` : '';
        label += ` (${app.displayName || app.name}${appPlatformString})`;
    }

    if (entityType === 'page' && parameterId) {
        label += ` (Parameter: ${parameterId})`;
    }

    return label;
}

// refers to the name of a column defined in the `pendo-aggregation` repository.
function buildFunctionName (functionName, entityType) {
    if (entityType) {
        // trackType uses camelCase (e.g., trackTypeEventCount)
        if (entityType === 'trackType') {
            return `${entityType + upperFirst(functionName)}`;
        }
        // agentic uses the base function name since the source is already filtered to agentic events
        if (entityType === 'agentic') {
            return functionName;
        }

        // page, feature, guide, etc. use underscore prefix (e.g., page_eventCount)
        return `${entityType}_${functionName}`;
    }

    return functionName;
}
